import { ENEMY_SIZE } from '../../constants';
import { rollDrop, rollGold, rollRuneDrop } from '../items';
import { spawnGroundDrop } from '../../hooks/gameLoop/helpers';

// ── Level helper ──────────────────────────────────────────────────────────────

/** Current level for a spell (defaults to 1). */
export const lvl = (s, id) => (s.runSpellLevels ?? {})[id] ?? 1;

// ── Stat-scaling helpers ──────────────────────────────────────────────────────

/**
 * Scale spell damage by the player's attack power.
 * At ATK 1 (default), result === base. At ATK 10, result === 10× base.
 */
export const sDmg = (s, base) => Math.ceil(base * Math.max(1, s.attackDamage ?? 1));

/**
 * Scale an AoE radius by the player's range multiplier.
 * At rangeMult 1 (default), result === base. At rangeMult 1.5, result === 1.5× base.
 */
export const sRad = (s, base) => Math.round(base * (s.rangeMult ?? 1));

/**
 * Scale a target count by ATK: +1 per 2 ATK above 1.
 * Ensures hit spells target more enemies as the player grows stronger.
 */
export const sCount = (s, base) => base + Math.floor(Math.max(0, (s.attackDamage ?? 1) - 1) * 0.5);

// ── Geometry helper ───────────────────────────────────────────────────────────

/**
 * Returns true if enemy centre (ex, ey) lies within the directional cone.
 * facing    — player facing angle in radians (atan2 direction, same as playerFacingAngle)
 * halfAngle — half the cone spread in radians (±halfAngle either side of facing)
 * range     — maximum distance in px
 */
export function inCone(ex, ey, cx, cy, facing, halfAngle, range) {
  const dx = ex - cx, dy = ey - cy;
  if (Math.hypot(dx, dy) > range) return false;
  let diff = Math.atan2(dy, dx) - facing;
  while (diff >  Math.PI) diff -= 2 * Math.PI;
  while (diff < -Math.PI) diff += 2 * Math.PI;
  return Math.abs(diff) <= halfAngle;
}

// ── Combat helpers ────────────────────────────────────────────────────────────

/**
 * Deal damage to one enemy; apply player crit; roll drop + update stats if it dies.
 * Returns updated enemy or null (dead).
 * Drops (items, runes, XP orbs) are spawned as visible ground drops identical
 * to melee kills so the player can collect them.
 */
export function hitEnemy(s, e, dmg) {
  const isCrit   = Math.random() < (s.critRate ?? 0.05);
  const finalDmg = isCrit ? Math.round(dmg * (s.critDamage ?? 1.5)) : dmg;
  const newHp    = (e.hp ?? 1) - finalDmg;
  if (newHp <= 0) {
    const extra       = s.activeModifier?.dropChanceBonus ?? 0;
    const ez          = e.size ?? ENEMY_SIZE;
    const ex          = e.x + ez / 2;
    const ey          = e.y + ez / 2;
    if (e.isBoss) s.bossKills = (s.bossKills ?? 0) + 1;
    // ── Tier Boss special kill (spell source) ────────────────────────────────
    if (e.isTierBoss) {
      s.tierBossAlive        = false;
      s.tierBossChestPending = true;
      for (let xi = 0; xi < 5; xi++) {
        const angle = (xi / 5) * Math.PI * 2;
        spawnGroundDrop(s, 'xp', ex + Math.cos(angle) * 55 - 10, ey + Math.sin(angle) * 55 - 10, 22);
      }
      s.xp    += (s.xpPerKill ?? 1) * (s.activeModifier?.xpMult ?? 1);
      s.kills  = (s.kills ?? 0) + 1;
      return null;
    }
    const playerLevel = (s.score ?? 0) / 1000;
    const magicFind   = s.magicFind ?? 0;
    const drop = rollDrop(extra, playerLevel, magicFind, s.currentTier ?? 0);
    if (drop) spawnGroundDrop(s, 'item', ex + (Math.random() - 0.5) * 14 - 12, ey + (Math.random() - 0.5) * 14 - 12, 24, drop);
    const runeDrop = rollRuneDrop(extra, (e.isBoss ? (s.currentTier ?? 0) * 2 : (e.tierIdx ?? 0) * 2), magicFind);
    if (runeDrop) spawnGroundDrop(s, 'item', ex + (Math.random() - 0.5) * 14 - 12, ey + (Math.random() - 0.5) * 14 - 12, 24, runeDrop);
    if (Math.random() < 0.5) spawnGroundDrop(s, 'xp', ex + (Math.random() - 0.5) * 20 - 8, ey + (Math.random() - 0.5) * 20 - 8, 24);
    const goldBase = rollGold(e.isBoss, s.goldFindBonus ?? 0);
    const gold = goldBase > 0 ? Math.round(goldBase * (s.activeModifier?.goldMult ?? 1)) : 0;
    if (gold > 0) s.goldThisRun = (s.goldThisRun ?? 0) + gold;
    s.xp    += (s.xpPerKill ?? 1) * (s.activeModifier?.xpMult ?? 1);
    s.kills  = (s.kills ?? 0) + 1;
    return null;
  }
  return { ...e, hp: newHp };
}

/**
 * Remove a set of enemy IDs from state, roll drops, update xp/kills.
 * Returns kill count.
 */
export function killSet(s, ids) {
  const extra       = s.activeModifier?.dropChanceBonus ?? 0;
  const playerLevel = (s.score ?? 0) / 1000;
  const magicFind   = s.magicFind ?? 0;
  let kills = 0;
  s.enemies = s.enemies.filter(e => {
    if (!ids.has(e.id)) return true;
    kills++;
    if (e.isBoss) s.bossKills = (s.bossKills ?? 0) + 1;
    const ez = e.size ?? ENEMY_SIZE;
    const ex = e.x + ez / 2;
    const ey = e.y + ez / 2;
    // ── Tier Boss special kill (spell source) ──────────────────────────────
    if (e.isTierBoss) {
      s.tierBossAlive        = false;
      s.tierBossChestPending = true;
      for (let xi = 0; xi < 5; xi++) {
        const angle = (xi / 5) * Math.PI * 2;
        spawnGroundDrop(s, 'xp', ex + Math.cos(angle) * 55 - 10, ey + Math.sin(angle) * 55 - 10, 22);
      }
      return false;
    }
    const drop = rollDrop(extra, playerLevel, magicFind, s.currentTier ?? 0);
    if (drop) spawnGroundDrop(s, 'item', ex + (Math.random() - 0.5) * 14 - 12, ey + (Math.random() - 0.5) * 14 - 12, 24, drop);
    const runeDrop = rollRuneDrop(extra, (e.isBoss ? (s.currentTier ?? 0) * 2 : (e.tierIdx ?? 0) * 2), magicFind);
    if (runeDrop) spawnGroundDrop(s, 'item', ex + (Math.random() - 0.5) * 14 - 12, ey + (Math.random() - 0.5) * 14 - 12, 24, runeDrop);
    if (Math.random() < 0.5) spawnGroundDrop(s, 'xp', ex + (Math.random() - 0.5) * 20 - 8, ey + (Math.random() - 0.5) * 20 - 8, 24);
    const goldBase = rollGold(e.isBoss, s.goldFindBonus ?? 0);
    const gold = goldBase > 0 ? Math.round(goldBase * (s.activeModifier?.goldMult ?? 1)) : 0;
    if (gold > 0) s.goldThisRun = (s.goldThisRun ?? 0) + gold;
    return false;
  });
  s.xp    += kills * (s.xpPerKill ?? 1) * (s.activeModifier?.xpMult ?? 1);
  s.kills  = (s.kills ?? 0) + kills;
  return kills;
}

// ── Nearest-targets utility ───────────────────────────────────────────────────

/** Return up to `count` enemies sorted nearest-first to centre (cx, cy). */
export function nearestTargets(s, cx, cy, count) {
  return [...s.enemies]
    .sort((a, b) =>
      Math.hypot(a.x + (a.size ?? ENEMY_SIZE) / 2 - cx, a.y + (a.size ?? ENEMY_SIZE) / 2 - cy) -
      Math.hypot(b.x + (b.size ?? ENEMY_SIZE) / 2 - cx, b.y + (b.size ?? ENEMY_SIZE) / 2 - cy)
    )
    .slice(0, count);
}
